import './styles.scss'
import React, { memo, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { uploadItemStatus } from '../../../ImageBrowser/constants'
import Loader from '../../../../../Shared/Loader'
import activityTrackingService from '../../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../../constants'
import { SortableContainer, SortableElement, sortableHandle } from 'react-sortable-hoc'
import { arrayMoveImmutable } from 'array-move'
import { ReactComponent as DragIcon } from '../../../../../../assets/img/icons/six-dots-drag-handle.svg'
import { ReactComponent as KebabIcon } from '../../../../../../assets/img/icons/three-dots-menu.svg'
import { ReactComponent as TrashCan } from '../../../../../Shared/Icon/img/trashcan.svg'
import Icon from '../../../../../Shared/Icon'
import { deleteImages } from '../../../../../../api'
import RoomImageContextMenu from '../RoomImageContextMenu'
import { useToast } from '../../../../../Shared/Toast'
import DeleteConfirmationOverlay from '../../../../../Shared/DeleteConfirmationOverlay'
import { isUploadQueueActive } from '../../../helpers'

const DraggableImageContainer = sortableHandle((props) => <div {...props} />)

const RoomImage = memo(({
                          image,
                          onIncludeInPropertyGallery,
                          onDelete,
                          isMenuOpen,
                          onMenuOpenChange,
                          galleries,
                          onImageUnderEditChange,
                          isSyncing,
                        }) => {
  const triggerRef = React.useRef(null)
  const isInPropertyGallery = galleries?.property?.order?.includes(image.id) ?? false
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const { t } = useTranslation()

  const toggleMenu = useCallback((e) => {
    e.stopPropagation()
    onMenuOpenChange(isMenuOpen ? null : image.id)
  }, [isMenuOpen, image.id, onMenuOpenChange])

  const closeMenu = useCallback(() => onMenuOpenChange(null), [onMenuOpenChange])

  return (
    <div className={`room-image-browser-item ${isMenuOpen ? 'menu-open' : ''} ${isSyncing ? 'syncing' : ''}`}>
      <DraggableImageContainer className="room-image-browser-item-image-container">
        <img
          className="room-image-browser-item-image"
          src={image.displayUrl}
          alt=""
        />
        <Icon
          cursor="move"
          extraClassNames="room-image-browser-item-image-drag-icon"
          size="small"
          icon={<DragIcon/>}
        />
      </DraggableImageContainer>
      <div className="room-image-browser-item-overlay">
        <button
          type="button"
          ref={triggerRef}
          className="room-image-browser-item-menu-trigger"
          onClick={toggleMenu}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
        >
          <KebabIcon/>
        </button>
        <RoomImageContextMenu
          show={isMenuOpen}
          target={triggerRef.current}
          onHide={closeMenu}
          isInPropertyGallery={isInPropertyGallery}
          onIncludeInPropertyGallery={() => {
            activityTrackingService.trackEvent(
              isInPropertyGallery
                ? trackingEvent.removeImageFromPropertyGallery
                : trackingEvent.addImageToPropertyGallery
            )
            onIncludeInPropertyGallery?.(!isInPropertyGallery, image.id)
            closeMenu()
          }}
          onDelete={() => {
            activityTrackingService.trackEvent(trackingEvent.deleteRoomImage)
            setShowDeleteConfirmation(true)
            closeMenu()
          }}
          onEditImage={() => {
            activityTrackingService.trackEvent(trackingEvent.editRoomImage)
            onImageUnderEditChange?.(image)
            closeMenu()
          }}
        />
      </div>
      {showDeleteConfirmation &&
        <DeleteConfirmationOverlay
          question={t('images.deleteConfirmationDialog.deleteQuestionSpecificImage')}
          information={t('images.deleteConfirmationDialog.imageDeletionInfo')}
          onCancel={() => setShowDeleteConfirmation(false)}
          onConfirmation={() => {
            onDelete?.(image.id)
            setShowDeleteConfirmation(false)
          }}
        />
      }
    </div>
  )
})

const SortableRoomImage = SortableElement(({
                                             image,
                                             onIncludeInPropertyGallery,
                                             onDelete,
                                             isMenuOpen,
                                             onMenuOpenChange,
                                             galleries,
                                             onImageUnderEditChange,
                                             syncingImageId,
                                           }) => <RoomImage
  image={image}
  onIncludeInPropertyGallery={onIncludeInPropertyGallery}
  onDelete={onDelete}
  isMenuOpen={isMenuOpen}
  onMenuOpenChange={onMenuOpenChange}
  galleries={galleries}
  onImageUnderEditChange={onImageUnderEditChange}
  isSyncing={syncingImageId === image.id}
/>)

const SortableImageList = SortableContainer(({
                                               imageMap,
                                               uploadQueue,
                                               onUploadQueueImageRemove,
                                               imagesOrder,
                                               onIncludeInPropertyGallery,
                                               onDelete,
                                               openMenuImageId,
                                               onMenuOpenChange,
                                               galleries,
                                               isMenuOpenForThisRoom,
                                               onImageUnderEditChange,
                                               syncingImageId,
                                             }) => (
  <div>
    {imagesOrder.filter(imageId => imageMap[imageId]).map((imageId, index) => {
      const image = imageMap[imageId]
      return (
        <SortableRoomImage
          key={`item-${image.id}`}
          index={index}
          image={image}
          onIncludeInPropertyGallery={onIncludeInPropertyGallery}
          onDelete={onDelete}
          isMenuOpen={openMenuImageId === image.id && isMenuOpenForThisRoom}
          galleries={galleries}
          onMenuOpenChange={onMenuOpenChange}
          onImageUnderEditChange={onImageUnderEditChange}
          syncingImageId={syncingImageId}
        />
      )
    })}
    {uploadQueue.map((uploadingImage, index) => (
      <RoomImageUploadItem
        image={uploadingImage}
        key={index + '_imageToUpload'}
        onUploadCancel={onUploadQueueImageRemove}
        onRemove={onUploadQueueImageRemove}
      />
    ))}
  </div>
))

const RoomImageUploadItem = memo((
  {
    image,
    onUploadCancel,
    onRemove
  }) => {
  switch (image.status) {
    case uploadItemStatus.uploading:
      if (image.progress === 100) {
        return (
          <div className="room-image-browser-item-preview">
            <div className="room-image-browser-item-image-preview-container">
              <img
                className="room-image-browser-item-image-preview image-browser-item-image-preview-blurred"
                src={image.src}
                alt=""
              />
              <div className="room-image-browser-item-image-preview-content">
                <div className="middle-content">Processing</div>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
              </div>
            </div>
          </div>
        )
      }
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container">
            <img
              className="room-image-browser-item-image-preview image-browser-item-image-preview-blurred"
              src={image.src}
              alt=""
            />
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <div>{image.progress}%</div>
                <Loader size={32}/>
              </div>
            </div>
          </div>
          <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
          </div>
        </div>
      )
    case uploadItemStatus.waiting:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} is queued.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">Queued</div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onUploadCancel(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    case uploadItemStatus.errorProcessImage:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} could not be processed. We
                recommend jpg files only.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <span className={'icon-item-image-preview-isolation-mode'}/>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onRemove(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    case uploadItemStatus.error:
      return (
        <div className="room-image-browser-item-preview">
          <div className="room-image-browser-item-image-preview-container"
               title={`File ${image.name} could not be processed.`}>
            <div className="room-image-browser-item-image-preview-content">
              <div className="middle-content">
                <span className={'icon-item-image-preview-isolation-mode'}/>
              </div>
              <div className="top-right-content">
                <span className={'icon-item-image-preview-close'}
                      onClick={() => onRemove(image)}/>
              </div>
            </div>
          </div>
        </div>
      )
    default:
      return null
  }
})

export default function RoomImageBrowser (
  {
    room,
    images,
    uploadQueue,
    onUploadQueueImageRemove,
    onUploadFiles,
    gallery,
    onGalleriesChange,
    galleries,
    onImagesDeleted,
    openMenuImageId,
    onMenuOpenChange,
    openMenuRoomId,
    onImageUnderEditChange,
  }
) {
  const { t } = useTranslation()
  const [optimisticOrder, setOptimisticOrder] = useState(null)
  const toast = useToast()
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncingImageId, setSyncingImageId] = useState(null)
  const isUploadingImages = useMemo(() => isUploadQueueActive(uploadQueue), [uploadQueue])
  const isImageOpsDisabled = isSyncing || isUploadingImages

  const imageMap = useMemo(() => images.reduce((map, image) => {
    map[image.id] = image
    return map
  }, {}), [images])

  const onSortEnd = useCallback(({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex || isSyncing) {
      return
    }
    activityTrackingService.trackEvent(trackingEvent.reorderRoomImage)
    const currentOrder = optimisticOrder || gallery?.order || images.map(i => i.id)
    const newOrder = arrayMoveImmutable(currentOrder, oldIndex, newIndex)

    // Optimistically update the UI
    setOptimisticOrder(newOrder)
    setIsSyncing(true)
    setSyncingImageId(currentOrder[oldIndex])

    onGalleriesChange({
      rooms: {
        [room.id]: {
          order: newOrder,
        },
      },
    })
      .catch(() => toast.error(
        <Trans i18nKey="editRooms.imageBrowser.toasts.reorderError"/>
      ))
      .finally(() => {
        setOptimisticOrder(null)
        setIsSyncing(false)
        setSyncingImageId(null)
      })
  }, [optimisticOrder, gallery, images, room, onGalleriesChange, isSyncing, toast])

  const onDrop = useCallback(files => {
    activityTrackingService.trackEvent(trackingEvent.uploadImages, {
      giataId: room.giataId,
      numberOfImages: files.length,
      page: 'Edit Rooms'
    })
    onUploadFiles(files, room.id)
  }, [onUploadFiles, room])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ['image/*'],
    disabled: isImageOpsDisabled,
  })

  const getUploadContainerClasses = () => {
    const classNames = [
      'room-image-browser-upload-container',
      'room-image-browser-upload-container-small',
      isDragActive && 'room-image-browser-upload-container-drag-active',
      isImageOpsDisabled && 'room-image-browser-upload-container-disabled',
    ]
    return classNames.filter(Boolean).join(' ')
  }

  const includeInPropertyGallery = (include, imageId) => {
    if (isImageOpsDisabled) {
      return
    }
    const image = imageMap[imageId]
    const imageFilename = image.filename ?? 'Image'
    const propertyGalleryOrder = [...galleries.property.order]
    if (include) {
      if (propertyGalleryOrder.includes(imageId)) {
        return
      }
      setIsSyncing(true)
      setSyncingImageId(imageId)
      onGalleriesChange({
        property: {
          order: [...propertyGalleryOrder, imageId]
        }
      })
        .then(() => toast.success(
          <Trans
            i18nKey="editRooms.imageBrowser.toasts.includeInPropertyGallerySuccess"
            values={{ filename: imageFilename }}
          />,
        ))
        .catch(() => toast.error(
          <Trans
            i18nKey="editRooms.imageBrowser.toasts.includeInPropertyGalleryError"
          />
        ))
        .finally(() => {
          setIsSyncing(false)
          setSyncingImageId(null)
        })
      return
    }
    // exclude from the property gallery
    if (!propertyGalleryOrder.includes(imageId)) {
      return
    }
    const newHeroImage = galleries.property.heroImage === imageId
      ? null
      : galleries.property.heroImage
    setIsSyncing(true)
    setSyncingImageId(imageId)
    onGalleriesChange({
      property: {
        order: propertyGalleryOrder.filter(id => id !== imageId),
        heroImage: newHeroImage
      }
    })
      .then(() => toast.success(
        <Trans
          i18nKey="editRooms.imageBrowser.toasts.excludeFromPropertyGallerySuccess"
          values={{ filename: imageFilename }}
        />
      ))
      .catch(() => toast.error(
        <Trans
          i18nKey="editRooms.imageBrowser.toasts.excludeFromPropertyGalleryError"
        />
      ))
      .finally(() => {
        setIsSyncing(false)
        setSyncingImageId(null)
      })
  }
  const deleteImage = (imageId) => {
    if (isImageOpsDisabled) {
      return
    }
    const image = imageMap[imageId]
    const filename = image.filename ?? 'Image'
    setIsSyncing(true)
    setSyncingImageId(imageId)
    deleteImages(room.giataId, [imageId]).fetchPromise.then(
      () => {
        onImagesDeleted([imageId])
        toast.success(
          <Trans i18nKey="editRooms.imageBrowser.toasts.deleteSuccess" values={{ filename }}/>, {
            icon: <TrashCan/>
          })
      }
    ).catch(() => {
      toast.error(
        <Trans i18nKey="editRooms.imageBrowser.toasts.deleteError" values={{ filename }}/>, {
          icon: <TrashCan/>
        }
      )
    }).finally(() => {
      setIsSyncing(false)
      setSyncingImageId(null)
    })
  }

  const roomImageBrowserId = 'room-image-browser-' + room.id
  const imagesOrder = optimisticOrder || gallery?.order || images.map(i => i.id)

  return (
    <div id={roomImageBrowserId} className={`room-image-browser ${isImageOpsDisabled ? 'image-ops-disabled' : ''}`}>
      <div className={getUploadContainerClasses()}>
        <div className={
          `room-image-browser-upload-button`}
             {...getRootProps()}>
          <input {...getInputProps()} />
          <div className={'room-image-browser-upload-button-icon'}></div>
          <div className={'room-image-browser-upload-button-desc'}>
            <span>{t('ADD_IMAGES')}</span>
            <span>{t('ADD_IMAGES_DESC')}</span>
          </div>
        </div>
      </div>
      <SortableImageList
        useDragHandle={true}
        imageMap={imageMap}
        imagesOrder={imagesOrder}
        uploadQueue={uploadQueue}
        onUploadQueueImageRemove={onUploadQueueImageRemove}
        onSortEnd={onSortEnd}
        axis="xy"
        helperContainer={() => document.getElementById(roomImageBrowserId)}
        helperClass="sorting-active-item"
        openMenuImageId={openMenuImageId}
        onMenuOpenChange={imageId => {
          onMenuOpenChange(imageId, room.id)
        }}
        isMenuOpenForThisRoom={openMenuRoomId === room.id}
        galleries={galleries}
        onIncludeInPropertyGallery={includeInPropertyGallery}
        onDelete={deleteImage}
        onImageUnderEditChange={onImageUnderEditChange}
        syncingImageId={syncingImageId}
        shouldCancelStart={() => isImageOpsDisabled}
      />
    </div>
  )
}
